import { Component } from '@theme/component';
import { debounce, normalizeString } from '@theme/utilities';

/**
 * Collection index component
 *
 * Filters an alphabetically grouped list of collection links based on the
 * value of a text input. Letter groups without visible items are hidden and
 * their anchor links are disabled.
 *
 * Tracks the currently visible letter group via IntersectionObserver and
 * highlights the corresponding anchor. Measures the sticky toolbar height
 * via ResizeObserver to keep scroll-margin-top in sync.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} filterInput
 * @property {HTMLElement} [toolbar]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [noResults]
 * @property {HTMLElement[]} [groups]
 * @property {HTMLAnchorElement[]} [anchors]
 * @property {HTMLLIElement[]} [items]
 * @property {HTMLButtonElement[]} [quickFilters]
 *
 * @extends {Component<Refs>}
 */
class CollectionIndexComponent extends Component {
  requiredRefs = ['filterInput'];

  /** @type {Set<string>} */
  #intersectingLetters = new Set();

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #stickyOffset = 0;

  /** @type {string} */
  #activeQuickFilter = '';

  /**
   * Handles input on the filter field, bound via `on:input="/filter"`.
   */
  filter = debounce(() => this.#applyFilter(), 150);

  connectedCallback() {
    super.connectedCallback();
    this.#updateStickyOffset();
    this.#setupIntersectionObserver();
    this.#setupResizeObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.filter.cancel();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  #updateStickyOffset() {
    const componentStyles = getComputedStyle(this);
    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const stickyBuffer = parseInt(componentStyles.getPropertyValue('--collection-index-sticky-buffer')) || 0;
    const toolbarHeight = parseInt(componentStyles.getPropertyValue('--toolbar-height')) || 0;

    this.#stickyOffset = headerHeight + stickyBuffer + toolbarHeight;
  }

  #setupIntersectionObserver() {
    this.#intersectionObserver?.disconnect();
    this.#intersectingLetters.clear();

    const headings = this.querySelectorAll('.collection-index__letter');
    if (!headings.length) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const letter = entry.target.closest('[data-letter]')?.dataset.letter;
          if (!letter) continue;

          if (entry.isIntersecting) {
            this.#intersectingLetters.add(letter);
          } else {
            this.#intersectingLetters.delete(letter);
          }
        }

        this.#updateActiveAnchor();
      },
      { rootMargin: `-${this.#stickyOffset}px 0px -60% 0px`, threshold: 0 }
    );

    headings.forEach((heading) => this.#intersectionObserver.observe(heading));
  }

  #updateActiveAnchor() {
    const anchors = this.refs.anchors ?? [];

    anchors.forEach((anchor) => anchor.removeAttribute('data-active'));

    if (this.#intersectingLetters.size === 0) return;

    const groups = this.refs.groups ?? [];
    for (const group of groups) {
      const letter = group.dataset.letter;
      if (this.#intersectingLetters.has(letter)) {
        const anchor = anchors.find((a) => a.dataset.letter === letter);
        anchor?.setAttribute('data-active', '');
        break;
      }
    }
  }

  #setupResizeObserver() {
    const toolbar = this.refs.toolbar;
    if (!toolbar) return;

    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = Math.round(entry.borderBoxSize[0].blockSize);
        this.style.setProperty('--toolbar-height', `${height}px`);
      }
      this.#updateStickyOffset();
      this.#setupIntersectionObserver();
    });

    this.#resizeObserver.observe(toolbar);
  }

  /**
   * Handles quick filter button clicks, bound via `on:click="/setQuickFilter"`.
   *
   * @param {Event} event
   */
  setQuickFilter(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;

    const selectedFilter = event.target.dataset.filter ?? '';
    this.#activeQuickFilter = selectedFilter === this.#activeQuickFilter ? '' : selectedFilter;
    this.#syncQuickFilterButtons();
    this.#applyFilter();
  }

  #syncQuickFilterButtons() {
    for (const filter of this.refs.quickFilters ?? []) {
      const isActive = (filter.dataset.filter ?? '') === this.#activeQuickFilter;

      filter.toggleAttribute('data-active', isActive);
      filter.setAttribute('aria-pressed', String(isActive));
    }
  }

  #applyFilter() {
    const query = normalizeString(this.refs.filterInput.value.trim());
    const anchors = this.refs.anchors ?? [];
    let total = 0;

    for (const group of this.refs.groups ?? []) {
      let visible = 0;

      for (const item of group.querySelectorAll('[ref="items[]"]')) {
        const title = item instanceof HTMLElement ? item.dataset.title ?? '' : '';
        const filters = item instanceof HTMLElement ? (item.dataset.filters ?? '').split(/\s+/).filter(Boolean) : [];
        const matchesQuery = query === '' || normalizeString(title).includes(query);
        const matchesQuickFilter = this.#activeQuickFilter === '' || filters.includes(this.#activeQuickFilter);
        const match = matchesQuery && matchesQuickFilter;

        item.toggleAttribute('hidden', !match);
        if (match) visible++;
      }

      group.toggleAttribute('hidden', visible === 0);

      const anchor = anchors.find((anchor) => anchor.dataset.letter === group.dataset.letter);
      anchor?.setAttribute('aria-disabled', String(visible === 0));
      anchor?.setAttribute('tabindex', visible === 0 ? '-1' : '0');

      total += visible;
    }

    this.refs.noResults?.toggleAttribute('hidden', total > 0);

    if (this.refs.status) {
      this.refs.status.textContent = (this.dataset.labelResultsCount ?? '[count]').replace('[count]', String(total));
    }

    this.#setupIntersectionObserver();
  }
}

if (!customElements.get('collection-index-component')) {
  customElements.define('collection-index-component', CollectionIndexComponent);
}
